/**
 * MobileHome — Redesigned mobile landing page
 * 5 Sections: Your Leagues, Quick Actions, League Activity, Free Agent Spotlight, News
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI, mlbAPI } from '../../../services/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import { MobilePlayerModal } from '../components/player/modal';
import { MobilePlayerCard } from '../components/player/card';
import MobileSectionHeader from '../components/MobileSectionHeader';
import QuickActions from '../components/QuickActions';
import LeagueActivityFeed from '../components/LeagueActivityFeed';
import FreeAgentSpotlight from '../components/FreeAgentSpotlight';
import useDefaultLeague from '../../../hooks/useDefaultLeague';

const GOLD = dynastyTheme.tokens.colors.primary;
const HAIRLINE = `1px solid ${dynastyTheme.tokens.colors.hairline}`;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

// Gold line icons for the app header (search + bell).
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" width="20" height="20">
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
};

const getRankSuffix = (rank) => {
  if (rank === 1) return 'st';
  if (rank === 2) return 'nd';
  if (rank === 3) return 'rd';
  return 'th';
};

const MobileHome = () => {
  const navigate = useNavigate();

  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headlines, setHeadlines] = useState([]);
  const [leaguesExpanded, setLeaguesExpanded] = useState(false);
  const [newsExpanded, setNewsExpanded] = useState(false);

  // Player modal/card state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  // Server-side default league preference
  const { defaultLeagueId } = useDefaultLeague(leagues, true);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLeaguesWithStandings();
      fetchHeadlines();
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const fetchLeaguesWithStandings = async () => {
    try {
      setLoading(true);
      const leaguesResponse = await leaguesAPI.getMyLeagues();
      if (!leaguesResponse.success || !leaguesResponse.leagues) {
        throw new Error('Failed to load leagues');
      }

      const userLeagues = leaguesResponse.leagues;

      const leaguesWithStandings = await Promise.all(
        userLeagues.map(async (league) => {
          try {
            const standingsResponse = await leaguesAPI.getLeagueStandings(league.league_id);
            if (standingsResponse.success && standingsResponse.standings) {
              const userTeam = standingsResponse.standings.find(
                team => team.team_id === league.team_id
              );
              if (userTeam) {
                const sortedStandings = [...standingsResponse.standings].sort(
                  (a, b) => (b.total_points || b.totalPoints || 0) - (a.total_points || a.totalPoints || 0)
                );
                const rank = sortedStandings.findIndex(team => team.team_id === league.team_id) + 1;
                return {
                  ...league,
                  teamName: userTeam.team_name || userTeam.teamName || league.team_name || '',
                  totalPoints: userTeam.total_points || userTeam.totalPoints || 0,
                  rank,
                  totalTeams: standingsResponse.standings.length,
                };
              }
            }
            return { ...league, totalPoints: null, rank: null, totalTeams: league.num_teams || 0 };
          } catch {
            return { ...league, totalPoints: null, rank: null, totalTeams: league.num_teams || 0 };
          }
        })
      );

      setLeagues(leaguesWithStandings);
      setError(null);
    } catch (err) {
      console.error('Error fetching leagues:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHeadlines = async () => {
    try {
      const res = await mlbAPI.getHeadlines();
      if (res?.success && res.headlines) {
        setHeadlines(res.headlines.slice(0, 10));
      }
    } catch {
      // Headlines are non-critical; silently fail
    }
  };

  // League card click
  const handleLeagueClick = (leagueId) => {
    navigate(`/leagues/${leagueId}`);
  };

  // Player modal handlers
  const handlePlayerClick = (playerId) => {
    setActivePlayerId(playerId?.toString());
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setActivePlayerId(null), 300);
  };

  const handleExpandToCard = () => {
    setIsModalOpen(false);
    setIsCardOpen(true);
  };

  const handleCollapseToModal = () => {
    setIsCardOpen(false);
    setIsModalOpen(true);
  };

  const handleCloseAll = () => {
    setIsCardOpen(false);
    setIsModalOpen(false);
    setTimeout(() => setActivePlayerId(null), 300);
  };

  // News click
  const handleNewsClick = (article) => {
    const url = article.link || article.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 15px',
          }}></div>
          <p style={{ color: dynastyTheme.tokens.colors.neutral[400] }}>
            Loading your leagues...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
        minHeight: '100vh',
        backgroundColor: 'transparent',
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: dynastyTheme.tokens.colors.error + '20',
          border: `1px solid ${dynastyTheme.tokens.colors.error}`,
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <p style={{ color: dynastyTheme.tokens.colors.error, marginBottom: '15px' }}>
            {error}
          </p>
          <button
            onClick={fetchLeaguesWithStandings}
            style={{
              padding: '10px 20px',
              backgroundColor: dynastyTheme.tokens.colors.primary,
              color: dynastyTheme.tokens.colors.black,
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const name = user?.given_name || user?.first_name || user?.firstName || user?.name || (user?.email ? user.email.split('@')[0] : '');
  const greeting = getGreeting();
  const defaultLeague = leagues.find(l => l.league_id === defaultLeagueId) || leagues[0] || null;
  const otherLeagues = leagues.filter(l => l !== defaultLeague);

  // Header search → the default league's player search (free-agents surface).
  const handleSearch = () => {
    const target = defaultLeagueId || (leagues[0] && leagues[0].league_id);
    if (target) navigate(`/leagues/${target}`, { state: { initialPage: 'free-agents' } });
  };

  // One flush league summary row (badge · name · team · pts · rank). No card.
  const renderLeagueRow = (league, isFirst) => (
    <div
      key={league.league_id}
      onClick={() => handleLeagueClick(league.league_id)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderTop: isFirst ? 'none' : HAIRLINE, cursor: 'pointer' }}
    >
      <div style={{ width: 30, height: 30, borderRadius: 6, backgroundColor: dynastyTheme.tokens.colors.primaryTint.t13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: GOLD, flexShrink: 0 }}>
        {(league.league_name || 'L')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: dynastyTheme.tokens.colors.textHi, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {league.league_name || 'Unnamed League'}
          {league.role === 'commissioner' && <span style={{ fontSize: 9, fontWeight: 700, color: GOLD, marginLeft: 6 }}>COM</span>}
        </div>
        {league.teamName ? <div style={{ fontSize: 10, color: dynastyTheme.tokens.colors.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{league.teamName}</div> : null}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: league.totalPoints != null ? GOLD : dynastyTheme.tokens.colors.muted, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
          {league.totalPoints != null ? league.totalPoints.toFixed(1) : '—'}
        </div>
        <div style={{ fontSize: 9, color: dynastyTheme.tokens.colors.muted, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
          {league.rank != null ? `${league.rank}${getRankSuffix(league.rank)} / ${league.totalTeams}` : '—'}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: 'transparent' }}>
      {/* Hide scrollbar globally for horizontal/vertical scroll windows */}
      <style>{`
        .home-scroll::-webkit-scrollbar { display: none; }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* ===== APP HEADER: greeting + search + bell (not boxed) ===== */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px 10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 20, fontWeight: 900, color: dynastyTheme.tokens.colors.textHi, lineHeight: 1.1, letterSpacing: '-0.3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {greeting}{name ? `, ${name}` : ''}
          </div>
          <div style={{ fontSize: 11, color: dynastyTheme.tokens.colors.muted, marginTop: 2 }}>Here's what's happening today</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, paddingLeft: 10 }}>
          <button onClick={handleSearch} aria-label="Search players" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <SearchIcon />
          </button>
          {/* Notifications affordance (bell). Notifications surface not yet built. */}
          <button aria-label="Notifications" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <BellIcon />
          </button>
        </div>
      </div>

      {/* ===== SECTION 1: YOUR LEAGUES (collapsible, flush rows) ===== */}
      <div>
        <MobileSectionHeader
          title="Your Leagues"
          chevron={otherLeagues.length > 0 ? { expanded: leaguesExpanded, onClick: () => setLeaguesExpanded(v => !v) } : undefined}
        />
        {defaultLeague && renderLeagueRow(defaultLeague, true)}
        {leaguesExpanded && otherLeagues.map((l) => renderLeagueRow(l, false))}
      </div>

      {/* ===== SECTION 2: QUICK ACTIONS ===== */}
      <QuickActions leagues={leagues} />

      {/* ===== SECTION 3: LEAGUE ACTIVITY FEED ===== */}
      <LeagueActivityFeed leagues={leagues} defaultLeagueId={defaultLeagueId} onPlayerClick={handlePlayerClick} />

      {/* ===== SECTION 4: WAIVER WIRE SPOTLIGHT ===== */}
      <FreeAgentSpotlight
        defaultLeagueId={defaultLeagueId}
        leagues={leagues}
        onPlayerClick={handlePlayerClick}
      />

      {/* ===== SECTION 5: NEWS (semi-collapsible 150->340) ===== */}
      <div style={{ borderTop: HAIRLINE }}>
        <MobileSectionHeader
          title="News"
          accent={dynastyTheme.tokens.colors.info}
          chevron={headlines.length > 0 ? { expanded: newsExpanded, onClick: () => setNewsExpanded(v => !v) } : undefined}
        />
        {headlines.length > 0 ? (
          <div className="home-scroll" style={{ maxHeight: newsExpanded ? 340 : 150, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', padding: '0 8px' }}>
            {headlines.slice(0, 10).map((article, i) => (
              <div
                key={article.id || i}
                onClick={() => handleNewsClick(article)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: i < Math.min(headlines.length, 10) - 1 ? HAIRLINE : 'none' }}
              >
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: GOLD, marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: dynastyTheme.tokens.colors.textHi, fontSize: 12, fontWeight: 500, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {article.title || article.headline}
                  </div>
                  <div style={{ color: dynastyTheme.tokens.colors.muted, fontSize: 9, marginTop: 2 }}>
                    {article.source || 'MLB'} · {getRelativeTime(article.pubDate || article.published_at || article.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '16px 8px', color: dynastyTheme.tokens.colors.muted, fontSize: 12 }}>No headlines available</div>
        )}
      </div>

      {/* PLAYER MODAL */}
      <MobilePlayerModal
        playerId={activePlayerId}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onExpandToCard={handleExpandToCard}
        leagueId={defaultLeagueId || (leagues.length > 0 ? leagues[0].league_id : null)}
      />

      {/* PLAYER CARD */}
      <MobilePlayerCard
        playerId={activePlayerId}
        leagueId={defaultLeagueId || (leagues.length > 0 ? leagues[0].league_id : null)}
        isOpen={isCardOpen}
        onClose={handleCloseAll}
        onCollapse={handleCollapseToModal}
      />
    </div>
  );
};

export default MobileHome;
